<script setup lang="ts">
import { computed, markRaw, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { formattedCompanyNeedAddress } from "@/helpers/address";

import type { FetchCompanyAddressResponse } from "@/modules/clients/types";
import type { CompanyNeedAddress } from "@/modules/companyNeed/types/address";
import type { IAddress } from '@/types/address';

import { BadgeColor, BadgeSize } from "@/dsComponents/Badge";
import Badge from "@/dsComponents/Badge/Badge.vue";
import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import AddressAutocomplete from "@/dsComponents/inputs/AddressAutocomplete.vue";
import Modal from "@/dsComponents/modals/Modal.vue";
import TagTitle from "@/dsComponents/modals/TagTitle.vue";
import TableAlt from "@/dsComponents/TableAlt/TableAlt.vue";

import MapView from "@/components/MapView.vue";

interface CompanyNeedLocationModalProps {
    open: boolean;
    companyAddresses: FetchCompanyAddressResponse[];
    defaultAddress?: CompanyNeedAddress;
}

const props = defineProps<CompanyNeedLocationModalProps>();

const emit = defineEmits<{
    close: [];
    select: [address: CompanyNeedAddress];
}>();

const { t } = useI18n();

const TABLE_COLUMNS = [
    { field: "isLocated", width: "22px", header: "" },
    { field: "name", width: "220px", header: "" },
    { field: "formattedAddress", width: "auto", header: "" },
];

const addressInput = ref("");
const selectedAddress = ref<CompanyNeedAddress>();
const isAutocompleteFocused = ref(true);

watch(
    () => props.open,
    (isOpen) => {
        if (isOpen && props.defaultAddress) {
            selectedAddress.value = { ...props.defaultAddress };
            addressInput.value = formattedCompanyNeedAddress(props.defaultAddress);
            isAutocompleteFocused.value = false;
            return;
        }

        selectedAddress.value = undefined;
        addressInput.value = "";
        isAutocompleteFocused.value = true;
    }
);

const isLocated = (address?: { latitude?: number; longitude?: number }) => {
    return !!address?.latitude && !!address?.longitude;
};

const mapResponseToAddress = (response: FetchCompanyAddressResponse): CompanyNeedAddress => ({
    street: response.street ?? "",
    city: response.city ?? "",
    zipCode: response.zip ?? "",
    department: response.department ?? "",
    region: response.region ?? "",
    latitude: response.latitude ?? 0,
    longitude: response.longitude ?? 0,
    complement: response.complement ?? undefined,
    country: response.countryCode ?? undefined,
});

const mapIAddressToAddress = (source: IAddress): CompanyNeedAddress => ({
    street: source.street ?? "",
    city: source.city ?? "",
    zipCode: source.zip ?? "",
    department: "",
    region: "",
    latitude: source.location?.[1] ?? 0,
    longitude: source.location?.[0] ?? 0,
    complement: source.complement ?? undefined,
    country: source.countryCode ?? undefined,
});

const effectiveAddress = computed(() => selectedAddress.value ?? props.defaultAddress);

const canSave = computed(() => !!effectiveAddress.value && isLocated(effectiveAddress.value));

const tableData = computed(() =>
    props.companyAddresses.map((response) => {
        const address = mapResponseToAddress(response);
        return {
            content: {
                isLocated: {
                    customElement: {
                        component: markRaw(Badge),
                        props: {
                            size: BadgeSize.sm,
                            icon: "world-pin",
                            iconColor: isLocated(address) ? BadgeColor.green : BadgeColor.red,
                        },
                    },
                },
                name: { text: { value: response.name || "" } },
                formattedAddress: { text: { value: formattedCompanyNeedAddress(address) } },
            },
            editMode: false,
        };
    })
);

const mapMarkers = computed(() => {
    if (!effectiveAddress.value || !isLocated(effectiveAddress.value)) return [];

    return [
        {
            id: 0,
            longitude: effectiveAddress.value.longitude,
            latitude: effectiveAddress.value.latitude,
            name: formattedCompanyNeedAddress(effectiveAddress.value),
        },
    ];
});

const handleInputChange = (value: string) => {
    isAutocompleteFocused.value = true;
    addressInput.value = value;
};

const handleSelectAutocomplete = (address: IAddress | null) => {
    if (!address) return;
    selectedAddress.value = mapIAddressToAddress(address);
    isAutocompleteFocused.value = false;
};

const handleSelectSavedAddress = (rowIndex: number) => {
    const address = mapResponseToAddress(props.companyAddresses[rowIndex]);
    addressInput.value = formattedCompanyNeedAddress(address);
    selectedAddress.value = address;
    isAutocompleteFocused.value = !isLocated(address);
};

const handleSave = () => {
    if (!effectiveAddress.value || !isLocated(effectiveAddress.value)) return;
    emit("select", effectiveAddress.value);
};

const handleClose = () => {
    emit("close");
};
</script>

<template>
    <Modal position="top" :open="open" @close="handleClose" width="720px" close action center-header :z-index-level="1">
        <template #header>
            <TagTitle :label="t('modals.companyNeed.manage.form.labels.address')" />
        </template>
        <div class="company-need-location-modal">
            <div class="company-need-location-modal__form">
                <AddressAutocomplete
                    label=""
                    :placeholder="t('modals.companyNeed.manage.form.placeholders.address')"
                    :model-value="addressInput"
                    @update:model-value="handleInputChange"
                    @address="handleSelectAutocomplete"
                    width="100%"
                    required
                    alt
                    variant="transparent"
                    :focused="isAutocompleteFocused"
                />
                <div v-if="companyAddresses.length > 0" class="company-need-location-modal__saved">
                    <div class="company-need-location-modal__saved-title typo-xxs-regular">
                        {{ t("modals.companyNeed.manage.form.savedAddresses") }}
                    </div>
                    <div class="company-need-location-modal__saved-table">
                        <TableAlt :columns="TABLE_COLUMNS" :rows="tableData" row-hover row-click @row-click="handleSelectSavedAddress" />
                    </div>
                </div>
            </div>
            <div v-if="open" class="company-need-location-modal__map">
                <MapView :rows="mapMarkers" :zoom="9" />
            </div>
        </div>
        <template #footer>
            <div class="company-need-location-modal__footer">
                <ButtonAlt type="neutral" variant="ghost" :label="t('global.actions.cancel')" @click="handleClose" />
                <ButtonAlt variant="filled" :label="t('global.actions.save')" :disabled="!canSave" @click="handleSave" />
            </div>
        </template>
    </Modal>
</template>

<style scoped lang="scss">
.company-need-location-modal {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-xl, 16px);

    &__form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3xl, 24px);
    }

    &__saved {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xl, 16px);

        &-title {
            color: var(--text-sub-600, #494e61);
        }

        &-table {
            overflow-y: auto;
            max-height: 150px;
        }
    }

    &__map {
        height: 280px;
    }

    &__footer {
        display: flex;
        gap: var(--spacing-3xl, 24px);
        align-items: center;
        justify-content: flex-end;
    }
}
</style>
